import { Component, computed, ElementRef, OnInit, input, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { BaseViewComponent, OverflowSidebarItem, SidebarItemState } from '@sb/library/base';
import { SidebarScope } from '../sidebar-item/sidebar-item.component';
import { NavigationItemState } from '@sb/library/data-models';

@Component({
    selector: 'lib-sidebar-overflow-item',
    templateUrl: './sidebar-overflow-item.component.html',
    styleUrls: ['./sidebar-overflow-item.component.scss']
})
export class SidebarOverflowItemComponent extends BaseViewComponent implements OnInit {
    Scope = this.SetScope({ Requires: SidebarScope });

    readonly items = input<OverflowSidebarItem[]>([]);
    readonly triggerRef = viewChild.required<ElementRef<HTMLElement>>('triggerRef');
    readonly panelRef = viewChild<ElementRef<HTMLElement>>('panelRef');
    readonly bottomSheetRef = viewChild<ElementRef<HTMLElement>>('bottomSheetRef');

    panelOpen = false;
    panelClosing = false;
    panelStyle: Record<string, string> = {};
    private _hoverOpened = false;

    private _closeTimer?: ReturnType<typeof setTimeout>;
    private _animationTimer?: ReturnType<typeof setTimeout>;
    private _closeAnimation?: Animation;

    readonly hasActiveItem = computed(() => 
        this.items().some(item => item.linkActive === SidebarItemState.Active)
    );

    readonly activeItemState = computed(() => 
        this.hasActiveItem() ? SidebarItemState.Active : SidebarItemState.Inactive
    );
    
    readonly NavigationItemState = NavigationItemState;

    get usesTouchLayout(): boolean {
        return this.isTouchScreenDevice || this.isMobile || (this.isTablet && window.matchMedia("(pointer: coarse)").matches);
    }

    TogglePanel(): void {
        if (this.panelOpen && !this.panelClosing) {
            if (!this.usesTouchLayout && this._hoverOpened) {
                this._hoverOpened = false;
                this.KeepPanelOpen();
                return;
            }
            this.ClosePanel();
        } else {
            clearTimeout(this._animationTimer);
            this._closeAnimation?.cancel();
            this._closeAnimation = undefined;
            this.panelClosing = false;
            if (this.usesTouchLayout) {
                this.panelOpen = true;
            } else {
                this.OpenPanel();
            }
        }
    }

    OpenPanel(fromHover = false): void {
        clearTimeout(this._closeTimer);
        if (this.panelOpen) {
            return;
        }
        this._hoverOpened = fromHover;
        const rect = this.triggerRef().nativeElement.getBoundingClientRect();
        const appBarElement = document.querySelector('.sb-app-bar, .sb-app-bar-mobile');
        const appBarBottom = appBarElement?.getBoundingClientRect().bottom ?? 0;

        // Render hidden first so we can measure the panel's natural height before placing it.
        this.panelStyle = {
            top: `${Math.max(rect.top, appBarBottom)}px`,
            left: `${rect.right + 4}px`,
            minWidth: `${rect.right + 4}px`,
            visibility: 'hidden'
        };
        this.panelOpen = true;

        requestAnimationFrame(() => {
            if (!this.panelOpen) {
                return;
            }
            const panelHeight = this.panelRef()?.nativeElement.getBoundingClientRect().height ?? 0;

            // Start at the trigger and grow downward, but flip upward (never above the app-bar) if there isn't room below.
            const top = rect.top + panelHeight <= window.innerHeight
                ? rect.top
                : Math.max(appBarBottom, window.innerHeight - panelHeight);
            
            const finalTop = Math.max(appBarBottom, top);

            this.panelStyle = {
                top: `${finalTop}px`,
                left: `${rect.right + 4}px`,
                maxHeight: `calc(100vh - ${finalTop}px)`,
                minWidth: `${rect.right + 4}px`,
                visibility: 'visible'
            };
        });
    }

    SchedulePanelClose(): void {
        this._closeTimer = setTimeout(() => {
            this.panelOpen = false;
            this._hoverOpened = false;
        }, 350);
    }

    KeepPanelOpen(): void {
        clearTimeout(this._closeTimer);
    }

    ClosePanel(): void {
        clearTimeout(this._closeTimer);
        this._hoverOpened = false;
        if (this.usesTouchLayout && this.panelOpen) {
            if (this.panelClosing) {
                return;
            }

            this.panelClosing = true;
            const sheet = this.bottomSheetRef()?.nativeElement
                ?? document.querySelector<HTMLElement>('.sb-sidebar-overflow-bottom-sheet');
            if (!sheet) {
                this._animationTimer = setTimeout(() => {
                    this.panelOpen = false;
                    this.panelClosing = false;
                }, 250);
                return;
            }

            const animation = sheet.animate(
                [{ transform: 'translateY(0)' }, { transform: 'translateY(100%)' }],
                { duration: 250, easing: 'ease-in', fill: 'forwards' }
            );
            this._closeAnimation = animation;
            this._animationTimer = setTimeout(() => {
                if (this._closeAnimation !== animation) {
                    return;
                }
                this.panelOpen = false;
                this.panelClosing = false;
                this._closeAnimation = undefined;
                this._animationTimer = undefined;
            }, 275);
            return;
        }
        this.panelOpen = false;
    }

    ClosePanelFromKeyboard(): void {
        this.ClosePanel();
        this.triggerRef().nativeElement.querySelector<HTMLButtonElement>('button')?.focus();
    }

    HandleFocusOut(event: FocusEvent, fromPanel: boolean): void {
        const relatedTarget = event.relatedTarget;
        const trigger = this.triggerRef().nativeElement;
        const panel = this.panelRef()?.nativeElement ?? this.bottomSheetRef()?.nativeElement;

        if (relatedTarget instanceof Node && panel?.contains(relatedTarget)) {
            return;
        }

        if (!fromPanel && relatedTarget instanceof Node && trigger.contains(relatedTarget)) {
            return;
        }

        this.ClosePanel();
    }

    SelectOverflowItem(): void {
        this.ClosePanel();
        if (this.usesTouchLayout) {
            this.Scope.CloseTheSidePanel!();
        }
    }
}
